import { useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { fetchROC } from '@/adapters/fetchExaCare';
import { useSelectedFacilityId } from '@/hooks/useFacilitiesQuery';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';

import {
  OutboundReferralModel,
  type OutboundReferralsResponse
} from '../models/OutboundReferralModel';

export const FIND_ALL_OUTBOUND_REFERRALS_QUERY_KEY = 'findAllOutboundReferrals';

const POLL_INTERVAL_MS = 30_000;

export function useOutboundReferralsQuery() {
  const queryClient = useQueryClient();
  const selectedFacilityId = useSelectedFacilityId();
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const facilityIdParam = isAllFacilitiesSelected
    ? undefined
    : (selectedFacilityId ?? undefined);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      queryClient.refetchQueries({
        queryKey: [FIND_ALL_OUTBOUND_REFERRALS_QUERY_KEY],
        refetchPage: (_page: unknown, index: number) => index === 0
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [queryClient]);

  return useInfiniteQuery(
    [
      FIND_ALL_OUTBOUND_REFERRALS_QUERY_KEY,
      isAllFacilitiesSelected ? 'all' : facilityIdParam
    ],
    async ({ pageParam }: { pageParam?: string }) => {
      const response = await fetchROC.get<OutboundReferralsResponse>(
        '/referrals/outbound',
        {
          searchParams: {
            limit: 50,
            ...(facilityIdParam ? { facility_id: facilityIdParam } : {}),
            ...(pageParam ? { cursor: pageParam } : {})
          }
        }
      );
      return {
        ...response,
        items: response.items.map((item) => new OutboundReferralModel(item))
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor ?? undefined
    }
  );
}
